import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface SearchableDropdownProps {
 fetchOptions: (search: string) => any;
 placeholder?: string;
 onSelect: (option: any) => void;
 value?: string; // Selected value ID
 getDisplayLabel?: (value: string) => Promise<string> | string; // Function to get display label for selected value
 searchable?: boolean; // Allow typing to search/filter options (default: true)
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
 fetchOptions,
 placeholder,
 onSelect,
 value,
 getDisplayLabel,
 searchable = true,
}) => {
 const [searchQuery, setSearchQuery] = useState("");
 const [isOpen, setIsOpen] = useState(false);
 const [options, setOptions] = useState([]);
 const [displayValue, setDisplayValue] = useState<string>("");
 const searchInputRef = useRef<HTMLInputElement>(null);
 const dropdownRef = useRef<HTMLDivElement>(null);
 const wasSearchFocusedRef = useRef(false);
 const getDisplayLabelRef = useRef(getDisplayLabel);
 const prevValueRef = useRef<string | undefined>(undefined);
 const prevGetDisplayLabelRef = useRef(getDisplayLabel);
 const isUpdatingDisplayRef = useRef(false);
 const isClickingInsideRef = useRef(false);
 const { t } = useTranslation();
 
 // Note: getDisplayLabel ref is now updated in the main display value effect below
 
 const updateOptions = useCallback(async () => {
  // When not searchable, always pass empty string to fetchOptions (no filtering)
  const query = searchable ? searchQuery : "";
  const options = await fetchOptions(query);
  setOptions(options as any);
 }, [fetchOptions, searchQuery, searchable]);
 
 // Update display value when value prop changes OR when getDisplayLabel function changes
 useEffect(() => {
  const valueChanged = prevValueRef.current !== value;
  const getDisplayLabelChanged = prevGetDisplayLabelRef.current !== getDisplayLabel;
  
  // Update refs
  if (valueChanged) {
   prevValueRef.current = value;
  }
  if (getDisplayLabelChanged) {
   prevGetDisplayLabelRef.current = getDisplayLabel;
   getDisplayLabelRef.current = getDisplayLabel;
  }
  
  // Only update if value OR getDisplayLabel actually changed
  if (!valueChanged && !getDisplayLabelChanged) {
   return;
  }
  
  // Prevent concurrent updates
  if (isUpdatingDisplayRef.current) {
   return;
  }
  
  // If value is undefined/null, clear display value and return early
  // Note: Empty string is a valid option value, so we don't treat it as "no selection"
  if (value === undefined || value === null) {
   setDisplayValue((prev) => {
    if (prev === "") return prev; // Already empty, no update needed
    return "";
   });
   return;
  }
  
  // Only call getDisplayLabel if we have the function (value can be empty string, which is valid)
  if (!getDisplayLabelRef.current) {
   return;
  }
  
  isUpdatingDisplayRef.current = true;
  const updateDisplay = async () => {
   try {
    const label = getDisplayLabelRef.current!(value);
    const finalLabel = typeof label === "string" ? label : await label;
    
    // Use setTimeout to ensure this happens after current render cycle
    setTimeout(() => {
     setDisplayValue((prev) => {
      // Only update if different to prevent loops
      if (prev === finalLabel) {
       isUpdatingDisplayRef.current = false;
       return prev;
      }
      isUpdatingDisplayRef.current = false;
      return finalLabel;
     });
    }, 0);
   } catch (error) {
    isUpdatingDisplayRef.current = false;
    // Silently handle errors to prevent crashes
    console.error("Error updating display label:", error);
   }
  };
  updateDisplay();
 }, [value, getDisplayLabel]);
 
 // Also update when getDisplayLabel function reference changes (even if value hasn't changed)
 // This handles the case where options load after the component mounts
 
 // Force update display value when options change (categories might have loaded)
 // DISABLED: This was causing infinite loops. The value-based effect should be sufficient.
 // If options change and we need to update the display, it will happen when value changes.
 // const prevOptionsRef = useRef(options);
 // useEffect(() => {
 //   // This effect was causing infinite loops - disabled for now
 // }, [options, value, isOpen]);
 
 useEffect(() => {
  updateOptions();
 }, [updateOptions]);

 // Maintain focus on search input
 useEffect(() => {
  if (wasSearchFocusedRef.current && searchInputRef.current && document.activeElement !== searchInputRef.current) {
   const cursorPosition = searchInputRef.current.selectionStart || searchQuery.length;
   searchInputRef.current.focus();
   searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
  }
 }, [searchQuery]);

 // Handle click outside to close dropdown
 // Note: This is a backup - the primary mechanism is the onBlur handler
 // which works even when modal stops event propagation
 useEffect(() => {
  if (!isOpen) return;

  const handleClickOutside = (event: MouseEvent | TouchEvent) => {
   const target = event.target as Node;
   if (
    dropdownRef.current &&
    !dropdownRef.current.contains(target)
   ) {
    setIsOpen(false);
   }
  };

  // Use capture phase to catch events before modal stops propagation
  document.addEventListener("mousedown", handleClickOutside, true);
  document.addEventListener("click", handleClickOutside, true);
  document.addEventListener("touchstart", handleClickOutside, true);
  
  return () => {
   document.removeEventListener("mousedown", handleClickOutside, true);
   document.removeEventListener("click", handleClickOutside, true);
   document.removeEventListener("touchstart", handleClickOutside, true);
  };
 }, [isOpen]);

 const handleOptionClick = (option: any, event: React.MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
  onSelect(option);
  setIsOpen(false);
  setSearchQuery("");
  if (option.label) {
   setDisplayValue(option.label);
  } else {
   setDisplayValue("");
  }
 };
 
 // Show display value when not open/focused, search query when open/focused (only if searchable)
 const inputValue = isOpen && searchable ? searchQuery : displayValue;

 return (
  <div className="relative" ref={dropdownRef}>
   <input
    ref={searchInputRef}
    type="text"
    value={inputValue}
    readOnly={!searchable}
    onChange={(e) => {
     if (searchable) {
      setSearchQuery(e.target.value);
      setDisplayValue(""); // Clear display value when user starts typing
     }
    }}
    onClick={() => {
     // Open dropdown on click (especially important for readonly inputs)
     if (!isOpen) {
      setIsOpen(true);
      wasSearchFocusedRef.current = true;
     }
    }}
    onFocus={() => {
     setIsOpen(true);
     wasSearchFocusedRef.current = true;
     // When focusing, clear search query to allow fresh search (only if searchable)
     if (searchable && displayValue && !searchQuery) {
      setSearchQuery("");
     }
    }}
    onBlur={(e) => {
     // Primary mechanism for closing dropdown when clicking outside
     // Use a delay to allow click events on options to complete first
     const relatedTarget = e.relatedTarget as Node | null;
     
     setTimeout(() => {
      // If we're clicking inside (on an option), don't close
      if (isClickingInsideRef.current) {
       isClickingInsideRef.current = false;
       return;
      }
      
      wasSearchFocusedRef.current = false;
      
      // Check if focus moved to an element inside the dropdown
      const activeElement = document.activeElement;
      const isFocusInsideDropdown = 
        (activeElement && dropdownRef.current?.contains(activeElement)) ||
        (relatedTarget && dropdownRef.current?.contains(relatedTarget));
       
      // Close if focus is not inside the dropdown
      if (!isFocusInsideDropdown) {
       setIsOpen(false);
      }
     }, 200);
    }}
    placeholder={placeholder || t("select")}
    className={`w-full px-3 py-2 border border-store-accent-light bg-white
          focus:outline-none focus:ring-2 focus:ring-store-secondary focus:border-store-secondary
          text-store-primary placeholder:text-store-primary-light
          transition-all duration-200
          ${!searchable ? "cursor-pointer" : ""}`}
   />
   {isOpen && options?.length > 0 && (
    <ul className="absolute z-50 w-full bg-white border border-store-accent-light mt-1 
           max-h-48 overflow-y-auto ">
     {options.map((option: any, index: number) => (
      <li
       key={option._id || index}
       onMouseDown={(e) => {
        isClickingInsideRef.current = true;
        e.preventDefault(); // Prevent input blur
       }}
       onClick={(e) => {
        handleOptionClick(option, e);
       }}
       className="px-4 py-2 cursor-pointer text-sm text-store-primary 
             hover:bg-store-secondary hover:bg-opacity-30 
             transition-all duration-200
             active:bg-store-accent-light active:bg-opacity-50"
      >
       {option.label}
      </li>
     ))}
    </ul>
   )}
   {isOpen && options?.length === 0 && searchQuery && (
    <div className="absolute z-50 w-full bg-white border border-store-accent-light mt-1 
            px-4 py-3">
     <p className="text-sm text-store-primary-light">No results found</p>
    </div>
   )}
  </div>
 );
};

export default SearchableDropdown;
